import { gql } from "@apollo/client";
import {
  channelMetaFields,
  episodeMetaFields,
  liveMetaFields,
  pageInfoFields,
  tvShowMetaFields,
  entitlementFields,
} from "./fragments";

export const userFavoritesFields = gql`
  ${episodeMetaFields}
  ${liveMetaFields}
  ${tvShowMetaFields}
  ${channelMetaFields}
  ${pageInfoFields}
  fragment userFavoritesFields on UserFavorites {
    id
    metas {
      ... on EpisodeMeta {
        id
      }
      ... on LiveMeta {
        id
      }
      ... on TVShowMeta {
        id
      }
      ... on ChannelMeta {
        id
      }
    }
  }
`;

export const profileFields = gql`
  fragment profileFields on ProfileUser {
    uuid
    id
    displayName: display_name
    birthday
    age
    sex
    ownerProfile
    needPasscode
    icon
    rating
  }
`;

export const accountFields = gql`
  ${profileFields}
  fragment accountFields on AccountUser {
    id
    uuid
    displayName: display_name
    birthday
    age
    sex
    mailAddress: mail_address
    firstName: first_name
    lastName: last_name
    fullName
    rightsFreeFlag: rights_free_flag
    currentProfile {
      ...profileFields
    }
    profiles {
      ...profileFields
    }
    test_flg
    hashedUserId
    receiveAdvertisingMailType
  }
`;

export const AUTH_LOGIN_MUTATION = gql`
  ${accountFields}
  mutation AuthLogin(
    $loginid: String!
    $password: String!
    $signature: String!
  ) {
    login(loginid: $loginid, password: $password, signature: $signature) {
      token
      error {
        code
      }
    }
  }
`;

export const AUTH_USER = gql`
  ${accountFields}
  query AuthUser {
    user {
      ...accountFields
    }
  }
`;

export const AUTH_USER_WITH_FAVORITES = gql`
  query AuthUserWithFavorites {
    user {
      ...accountFields
    }
    favorites: userFavorites {
      ...userFavoritesFields
    }
  }
`;

export const AUTH_LOGOUT_MUTATION = gql`
  mutation AuthLogout($force: Boolean = false) {
    logout(force: $force) {
      result
    }
  }
`;

export const AUTH_SELECT_PROFILE_MUTATION = gql`
  ${accountFields}
  mutation AuthSelectProfile(
    $profileId: Int!
    $passcode: String
    $type: String
  ) {
    selectProfile(profileId: $profileId, passcode: $passcode, type: $type) {
      ...accountFields
    }
  }
`;

export const AUTH_PROFILE_MUTATION = gql`
  mutation AuthProfile($profileId: Int, $passcode: String) {
    authProfile(profileId: $profileId, passcode: $passcode) {
      result
    }
  }
`;

export const ACCOUNT_SIGNUP_PINCODE_MUTATION = gql`
  mutation AccountSignupPinCode($mailAddress: String!) {
    signupPincode(mailAddress: $mailAddress) {
      result
    }
  }
`;

export const ACCOUNT_SEND_PINCODE_MUTATION = gql`
  mutation AccountSendPinCode($mailAddress: String!) {
    sendPincode(mailAddress: $mailAddress) {
      result
    }
  }
`;

export const ACCOUNT_SEND_PROFILE_RESET_TOKEN = gql`
  mutation AccountSendProfilePinResetToken {
    sendProfilePinResetToken {
      result
    }
  }
`;

export const ACCOUNT_SEND_PASSWORD_RESET_TOKEN = gql`
  mutation AccountSendPasswordResetToken($mailAddress: String!) {
    sendPasswordResetToken(mailAddress: $mailAddress) {
      result
    }
  }
`;

export const ACCOUNT_PASSWORD_RESET = gql`
  mutation AccountPasswordReset($password: String!, $token: String!) {
    passwordReset(password: $password, token: $token) {
      result
    }
  }
`;

export const ACCOUNT_PASSCODE_RESET = gql`
  mutation AccountPasscodeReset($passcode: String!, $token: String!) {
    passcodeReset(passcode: $passcode, token: $token) {
      result
    }
  }
`;

export const ACCOUNT_VERIFY_PINCODE_MUTATION = gql`
  mutation AccountVerifyPinCode($code: String!, $mailAddress: String!) {
    verifyPinCode(code: $code, mailAddress: $mailAddress) {
      verified
      error
    }
  }
`;

export const ACCOUNT_VERIFY_PASSWORD_TOKEN_MUTATION = gql`
  mutation AccountVerifyPasswordToken($token: String!) {
    verifyPasswordToken(token: $token) {
      verified
      error
    }
  }
`;

export const ACCOUNT_SIGNUP_MUTATION = gql`
  ${accountFields}
  mutation AccountSignup(
    $code: String!
    $mailAddress: String!
    $password: String!
    $firstName: String!
    $lastName: String!
    $birthday: String!
    $sex: String!
    $terms: String!
    $signature: String!
  ) {
    signup(
      code: $code
      mailAddress: $mailAddress
      password: $password
      firstName: $firstName
      lastName: $lastName
      birthday: $birthday
      sex: $sex
      terms: $terms
      signature: $signature
    ) {
      token
      user {
        ...accountFields
      }
    }
  }
`;

export const ADD_FAVORITE_MUTATION = gql`
  ${userFavoritesFields}
  mutation AddFavorite($id: Int!) {
    addFavorite(id: $id) {
      result
      favorites {
        ...userFavoritesFields
      }
    }
  }
`;

export const DELETE_FAVORITE_MUTATION = gql`
  ${userFavoritesFields}
  mutation DeleteFavorite($id: Int!) {
    deleteFavorite(id: $id) {
      result
      favorites {
        ...userFavoritesFields
      }
    }
  }
`;

export const GET_FAVORITE_QUERY = gql`
  ${episodeMetaFields}
  ${liveMetaFields}
  ${tvShowMetaFields}
  ${channelMetaFields}
  ${pageInfoFields}
  query FavoritesByContentType($type: String, $offset: Int, $limit: Int) {
    favorites: favoritesByContentType(
      type: $type
      offset: $offset
      limit: $limit
    ) {
      data {
        ... on EpisodeMeta {
          ...episodeMetaFields
        }
        ... on LiveMeta {
          ...liveMetaFields
        }
        ... on TVShowMeta {
          ...tvShowMetaFields
        }
        ... on ChannelMeta {
          ...channelMetaFields
        }
      }
      pageInfo {
        ...pageInfoFields
      }
    }
  }
`;

export const VIEWING_HISTORIES = gql`
  ${episodeMetaFields}
  ${liveMetaFields}
  ${pageInfoFields}
  query ViewingHistories(
    $offset: Int
    $limit: Int
    $sort: String
    $nocache: Boolean = false
  ) {
    viewingHistories(
      offset: $offset
      limit: $limit
      sort: $sort
      nocache: $nocache
    ) {
      data {
        ... on EpisodeMeta {
          ...episodeMetaFields
          resume_point
          last_viewing_at
        }
        ... on LiveMeta {
          ...liveMetaFields
          resume_point
          last_viewing_at
        }
      }
      pageInfo {
        ...pageInfoFields
      }
    }
  }
`;

export const DELETE_VIEWING_HISTORY = gql`
  mutation DeleteViewingHistory($metaIds: [String]) {
    deleteViewingHistory(metaIds: $metaIds) {
      result
    }
  }
`;

export const ADD_RESUME_MUTATION = gql`
  mutation AddResume(
    $resume: Int
    $metaId: Int
    $groupedMetaId: Int
    $psid: String
    $reachToEnd: Boolean
  ) {
    addResume(
      resume: $resume
      metaId: $metaId
      groupedMetaId: $groupedMetaId
      psid: $psid
      reachToEnd: $reachToEnd
    ) {
      result
    }
  }
`;

export const ACCOUNT_UPDATE_MUTATION = gql`
  ${accountFields}
  mutation AccountUpdate($mailAddress: String, $code: String) {
    update(mailAddress: $mailAddress, code: $code) {
      user {
        ...accountFields
      }
      error
    }
  }
`;

export const ACCOUNT_UPDATE_PASSWORD_MUTATION = gql`
  ${accountFields}
  mutation AccountUpdatePassword($currentPassword: String, $password: String) {
    updatePassword(currentPassword: $currentPassword, password: $password) {
      user {
        ...accountFields
      }
      error
    }
  }
`;

export const ACCOUNT_UPDATE_OWNER_PROFILE_MUTATION = gql`
  ${accountFields}
  mutation AccountUpdateOwnerProfile(
    $displayName: String
    $icon: Int
    $passcode: String
    $rating: Int
  ) {
    updateOwnerProfile(
      displayName: $displayName
      icon: $icon
      passcode: $passcode
      rating: $rating
    ) {
      user {
        ...accountFields
      }
      error
    }
  }
`;

export const ACCOUNT_ADD_PROFILE_MUTATION = gql`
  ${profileFields}
  mutation AccountAddProfile(
    $displayName: String
    $birthday: String
    $sex: String
    $icon: Int
    $rating: Int
    $passcode: String
  ) {
    addProfile(
      displayName: $displayName
      birthday: $birthday
      sex: $sex
      icon: $icon
      rating: $rating
      passcode: $passcode
    ) {
      profile {
        ...profileFields
      }
    }
  }
`;

export const ACCOUNT_UPDATE_PROFILE_MUTATION = gql`
  ${profileFields}
  mutation AccountUpdateProfile(
    $displayName: String
    $birthday: String
    $sex: String
    $password: String
    $profileId: Int
    $icon: Int
    $rating: Int
    $passcode: String
  ) {
    updateProfile(
      displayName: $displayName
      birthday: $birthday
      sex: $sex
      password: $password
      profileId: $profileId
      icon: $icon
      rating: $rating
      passcode: $passcode
    ) {
      profile {
        ...profileFields
      }
    }
  }
`;

export const ACCOUNT_DELETE_PROFILE_MUTATION = gql`
  mutation AccountDeleteProfile($profileId: Int) {
    deleteProfile(profileId: $profileId) {
      result
    }
  }
`;

export const ACCOUNT_USER_DELETE = gql`
  mutation AccountUserDelete {
    userDelete {
      result
    }
  }
`;

// export const BROADCAST_DISCOUNT = gql`
//   query AccountBroadcastDiscount($courseId: Int, $productId: Int) {
//     broadcastDiscount(courseId: $courseId, productId: $productId) {
//       result
//       user_voucher_id
//     }
//   }
// `;

export const USER_ENTITLEMENTS = gql`
  ${entitlementFields}
  query UserEntitlements {
    entitlements: userEntitlements {
      ...entitlementFields
    }
  }
`;

export const ACCOUNT_ADVERTISING_PREFERENCES_MUTATION = gql`
  mutation AccountAdvertisingPreferences($receiveAdvertisingMailFlag: Boolean) {
    advertisingPreferences(
      receiveAdvertisingMailFlag: $receiveAdvertisingMailFlag
    ) {
      request_id
    }
  }
`;

export const CHECK_TOKEN_TIMEOUT = gql`
  query CheckTokenTimeout {
    checkTokenTimeout {
      result
    }
  }
`;

export const REFRESH_TOKEN = gql`
  mutation RefreshToken {
    refreshToken {
      result
    }
  }
`;

export const IDC_TOKEN_LOGIN_QUERY = gql`
  ${accountFields}
  mutation IdcTokenLogin($signature: String!) {
    idcTokenLogin(signature: $signature) {
      token
      user {
        ...accountFields
      }
      error {
        code
      }
    }
  }
`;

export const ACCOUNT_IDC_TOKEN_SIGNUP_MUTATION = gql`
  ${accountFields}
  mutation AccountIdcTokenSignup(
    $password: String!
    $terms: String!
    $signature: String!
  ) {
    idcTokenSignup(password: $password, terms: $terms, signature: $signature) {
      token
      user {
        ...accountFields
      }
    }
  }
`;

export const SPOOX_ID_IDC_TOKEN_MAPPING_MUTATION = gql`
  mutation SpooxIdIdcTokenMapping($signature: String!) {
    idcTokenMapping(signature: $signature) {
      user {
        ...accountFields
      }
    }
  }
`;
